import { Dispatch, useMemo, useState } from 'react'
import type { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core'
import {
  ArrowUpwardRounded,
  ReplayRounded,
  SendRounded,
} from '@material-ui/icons'
import Bugsnag from '../utils/bugsnag'

import Layout from '../components/Layout'
import Meta from '../components/Meta'
import Ladder from '../components/Ladder'
import PlayerPicker from '../components/PlayerPicker'
import AlertDialog from '../components/AlertDialog'

import { getFullUrl } from '../utils'
import { isEmptyStorageTiers, isFullStorageTiers } from '../utils/storage'
import {
  fromStorageTiers,
  getAvailablePlayers,
  getLadderUrl,
} from '../utils/player'
import { Action, getLadderFromState, usePersistReducer } from '../utils/reducer'
import { saveLadder } from '../api/client'
import { getAllPlayers } from '../api/server'
import type { Player, StorageLadder, StorageTier } from '../types'

const ResetButton = ({ onReset }: { onReset: () => void }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const handleClose = () => setShowConfirmDialog(false)

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ReplayRounded />}
        onClick={() => setShowConfirmDialog(true)}
      >
        Start over
      </Button>
      <Dialog
        open={showConfirmDialog}
        onClose={handleClose}
        aria-labelledby="confirm-reset-title"
        aria-describedby="confirm-reset-description"
      >
        <DialogTitle id="confirm-reset-title">Start over?</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-reset-description">
            Starting over will <strong>remove all player selections</strong>{' '}
            from all tiers in your ladder.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Nevermind
          </Button>
          <Button
            onClick={() => {
              onReset()
              handleClose()
            }}
          >
            Clear out ladder
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const PublishButton = ({
  canPublish,
  ladder,
  onFinish,
}: {
  canPublish: boolean
  ladder: StorageLadder
  onFinish: ({ id, title }: { id: string; title: string }) => void
}) => {
  const { spacing } = useTheme()
  const [showTitleDialog, setShowTitleDialog] = useState(false)
  const [title, setTitle] = useState('')
  const [alertDialogMessage, setAlertDialogMessage] = useState('')
  const [isPublished, setIsPublished] = useState(false)

  const handleTitleDialogClose = () => {
    setTitle('')
    setShowTitleDialog(false)
    setIsPublished(false)
  }
  const handleFinish = async () => {
    try {
      const ladderId = await saveLadder(ladder, title)

      onFinish({ id: ladderId, title })
      setIsPublished(true)
    } catch (err) {
      Bugsnag.notify(err, (event) => {
        event.addMetadata('state', { ...ladder, title })
      })

      setIsPublished(false)
      handleTitleDialogClose()
      setAlertDialogMessage(
        'An error occurred while publish your new ladder. Please try again.',
      )
    }
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        endIcon={<SendRounded />}
        disabled={!canPublish}
        onClick={() => setShowTitleDialog(true)}
        style={{ marginLeft: spacing(1) }}
      >
        Publish
      </Button>

      <Dialog
        open={showTitleDialog}
        onClose={handleTitleDialogClose}
        aria-labelledby="title-dialog-title"
        aria-describedby="title-dialog-description"
      >
        <DialogTitle id="title-dialog-title">
          Choose a title for your NBA ladder
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="title-dialog-description">
            To publish and share your NBA rankings with the world, please
            provide a title for your ladder.
          </DialogContentText>
          <TextField
            id="title-dialog-ladder-title"
            autoFocus
            margin="dense"
            label="Ladder title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTitleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={
              isPublished ? (
                <CircularProgress
                  size={24}
                  thickness={2.16}
                  color="secondary"
                />
              ) : (
                <SendRounded />
              )
            }
            disabled={!title || isPublished}
            onClick={handleFinish}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={!!alertDialogMessage}
        onClose={() => setAlertDialogMessage('')}
      >
        {alertDialogMessage}
      </AlertDialog>
    </>
  )
}

const PlayerUI = ({
  activeTierIndex,
  allPlayers,
  dispatch,
  tiers,
}: {
  activeTierIndex: number
  allPlayers: Player[]
  dispatch: Dispatch<Action>
  tiers: StorageTier[]
}) => {
  const [playerTiers, availablePlayers] = useMemo(
    () => [
      fromStorageTiers(tiers, allPlayers),
      getAvailablePlayers(allPlayers, tiers),
    ],
    [tiers, allPlayers],
  )

  return (
    <>
      <Box mt={2}>
        <Ladder
          tiers={playerTiers}
          onAddToTier={(index) => dispatch({ type: 'set_active_tier', index })}
          onClearTier={(index) => dispatch({ type: 'clear_tier', index })}
          onPlayerRemoveFromTier={(tierIndex, playerId) =>
            dispatch({ type: 'remove_player', playerId, tierIndex })
          }
        />
      </Box>

      <Drawer
        variant="temporary"
        anchor="right"
        open={activeTierIndex > -1}
        onClose={() => dispatch({ type: 'unset_active_tier' })}
      >
        <Box component="aside" width={350} maxWidth="80vw" p={2}>
          <PlayerPicker
            availablePlayers={availablePlayers}
            activeTierIndex={activeTierIndex}
            onSelect={(playerId) =>
              dispatch({ type: 'add_to_active_tier', playerId })
            }
          />
        </Box>
      </Drawer>
    </>
  )
}

interface Props {
  allPlayers: Player[]
}

const CreatePage = ({ allPlayers }: Props) => {
  const router = useRouter()
  const [state, dispatch] = usePersistReducer()
  const { activeTierIndex, tiers } = state

  const canReset = !isEmptyStorageTiers(tiers)
  const canPublish = isFullStorageTiers(tiers)

  const url = getFullUrl('/create')

  return (
    <Layout maxWidth="lg">
      <Meta
        url={url}
        title="Rank the NBA's top 25 players"
        description="Create your own NBA ladder by ranking the top 25 players in the NBA, breaking them up into five tiers. Who sits in your top tier?"
      />

      <Box id="title-container" mt={2}>
        <Typography component="h1" variant="h3">
          Rank your NBA Top 25
        </Typography>
      </Box>

      {canReset && (
        <Box mt={2} textAlign="center">
          <Typography align="center" gutterBottom>
            Make sure that you publish your ladder in order to share it with
            others and begin the debate.
          </Typography>
          <ResetButton onReset={() => dispatch({ type: 'reset' })} />
          <PublishButton
            canPublish={canPublish}
            ladder={getLadderFromState(state)}
            onFinish={({ id, title }) => {
              router.push(getLadderUrl({ id, title }))
            }}
          />{' '}
        </Box>
      )}

      <PlayerUI
        activeTierIndex={activeTierIndex}
        allPlayers={allPlayers}
        dispatch={dispatch}
        tiers={tiers}
      />

      {canPublish && (
        <Box mt={4} textAlign="center">
          <Button
            variant="outlined"
            endIcon={<ArrowUpwardRounded />}
            onClick={() =>
              document
                .getElementById('title-container')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            Finished?
          </Button>
        </Box>
      )}
    </Layout>
  )
}

export default CreatePage

const ONE_DAY = 60 * 60 * 24

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      allPlayers: await getAllPlayers(),
    },
    revalidate: ONE_DAY,
  }
}
